import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { DollarSign, Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentsTab({ onUpdate }) {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletePayment, setDeletePayment] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    amount: '0',
    payment_method: 'GCash',
    reference_number: '',
    notes: '',
    payment_date: new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
  });

  useEffect(() => {
    fetchPayments();
    fetchUsers();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await api.get('/admin/payments');
      setPayments(response.data.payments);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users?limit=1000');
      // Filter out banned users
      const activeUsers = response.data.users.filter(user => !user.is_banned);
      setUsers(activeUsers);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_id) {
      toast.error('Please select a user');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/payments', formData);
      toast.success('Payment recorded and subscription activated!');
      setShowAddForm(false);
      setFormData({
        user_id: '',
        amount: '699',
        payment_method: 'GCash',
        reference_number: '',
        notes: '',
        payment_date: new Date().toISOString().split('T')[0]
      });
      fetchPayments();
      onUpdate();
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if it's an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Failed to record payment');
        }
      } else {
        toast.error('Failed to record payment');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePayment) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/payments/${deletePayment.id}`);
      toast.success('Payment record deleted successfully');
      setDeletePayment(null);
      fetchPayments();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (payment) => {
    setEditPayment(payment);
    setEditFormData({
      amount: payment.amount?.toString() || '0',
      payment_method: payment.payment_method || 'GCash',
      reference_number: payment.reference_number || '',
      notes: payment.notes || '',
      payment_date: payment.payment_date 
        ? new Date(payment.payment_date).toISOString().split('T')[0]
        : new Date(payment.created_at).toISOString().split('T')[0]
    });
  };

  const handleEditSave = async () => {
    if (!editPayment) return;
    
    setSaving(true);
    try {
      await api.put(`/admin/payments/${editPayment.id}`, editFormData);
      toast.success('Payment record updated successfully');
      setEditPayment(null);
      fetchPayments();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Payments</CardTitle>
            <Button onClick={() => setShowAddForm(!showAddForm)} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Add Payment Form */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4">
              <h3 className="font-semibold">Record Payment</h3>
              
              <div>
                <Label>User</Label>
                <Select
                  value={formData.user_id}
                  onValueChange={(value) => setFormData({...formData, user_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount</Label>
                  <Select
                    value={formData.amount}
                    onValueChange={(value) => setFormData({...formData, amount: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">₱0 (Free Subscription)</SelectItem>
                      <SelectItem value="699">₱699 (First Year)</SelectItem>
                      <SelectItem value="99">₱99 (Annual Renewal)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Payment Method</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(value) => setFormData({...formData, payment_method: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GCash">GCash</SelectItem>
                      <SelectItem value="Maya">Maya</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Date of Payment Received</Label>
                <Input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <Label>Reference Number</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                  placeholder="Transaction reference or receipt number"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes about this payment"
                  rows={2}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Processing...' : 'Record Free Subscription'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Note: Use this for free subscriptions only. Paid subscriptions are automatically recorded when approved in Payment Orders.
              </p>
            </form>
          )}

          {/* Payments List */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading payments...</div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No payments recorded yet</div>
          ) : (
            <>
              {/* Mobile scroll hint */}
              <p className="text-xs text-gray-500 md:hidden mb-2 flex items-center gap-1 px-2">
                <DollarSign className="w-3 h-3 flex-shrink-0" />
                Scroll horizontally to see all columns →
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {payment.payment_date 
                            ? new Date(payment.payment_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                            : new Date(payment.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                          }
                        </div>
                        {payment.payment_date && (
                          <div className="text-xs text-gray-500">
                            Recorded: {new Date(payment.created_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{payment.user_name}</p>
                          <p className="text-xs text-gray-500">{payment.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-emerald-600">
                          ₱{payment.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{payment.payment_method}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {payment.reference_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {payment.notes || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openEditModal(payment)}
                            data-testid={`edit-payment-${payment.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeletePayment(payment)}
                            data-testid={`delete-payment-${payment.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletePayment} onOpenChange={(open) => !open && setDeletePayment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Payment Record</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this payment record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deletePayment && (
            <div className="bg-gray-50 rounded-lg p-4 my-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">User:</div>
                <div className="font-medium">{deletePayment.user_name}</div>
                <div className="text-gray-500">Amount:</div>
                <div className="font-medium text-emerald-600">₱{deletePayment.amount?.toLocaleString()}</div>
                <div className="text-gray-500">Method:</div>
                <div>{deletePayment.payment_method}</div>
                <div className="text-gray-500">Reference:</div>
                <div>{deletePayment.reference_number || '-'}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePayment(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={!!editPayment} onOpenChange={(open) => !open && setEditPayment(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payment Record</DialogTitle>
            <DialogDescription>
              Update the payment information for {editPayment?.user_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (₱)</Label>
              <Input
                type="number"
                value={editFormData.amount}
                onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Payment Method</Label>
              <Select
                value={editFormData.payment_method}
                onValueChange={(value) => setEditFormData({...editFormData, payment_method: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GCash">GCash</SelectItem>
                  <SelectItem value="Maya">Maya</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={editFormData.payment_date}
                onChange={(e) => setEditFormData({...editFormData, payment_date: e.target.value})}
              />
            </div>

            <div>
              <Label>Reference Number</Label>
              <Input
                value={editFormData.reference_number}
                onChange={(e) => setEditFormData({...editFormData, reference_number: e.target.value})}
                placeholder="Transaction reference"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                placeholder="Additional notes"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPayment(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
